/**
 * Hard gate for routes whose entire purpose is showing user-specific
 * data (watchlist, workspaces, ingest dashboard, etc.). Anonymous
 * users get redirected to / and the auth modal pops so they can sign
 * in and come back.
 *
 * Soft-gated pages (e.g. /proceedings/:docket) should NOT use this —
 * they stay open and gate individual actions via useRequireAuth.
 */

import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const RequireAuthRoute: React.FC<{
  children: React.ReactNode;
  /** Optional verb shown in the auth modal headline. */
  intentLabel?: string;
}> = ({ children, intentLabel }) => {
  const { user, loading, isAnonymous, openAuthModal } = useAuth();

  useEffect(() => {
    if (!loading && isAnonymous && !user) {
      openAuthModal({
        headline: intentLabel ? `Sign in to ${intentLabel}` : 'Sign in to continue',
      });
    }
  }, [loading, isAnonymous, user, intentLabel, openAuthModal]);

  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  return <>{children}</>;
};
